import { GoogleMap } from '@react-google-maps/api'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarker'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineBusAltIcon from 'LuxKit/Icons/line/LineBusAltIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Typography from 'LuxKit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pickupTypeLabelMap } from 'constants/carHire'
import { SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { EnsureGoogleMapsLoaded } from 'contexts/GoogleMapsContext'
import useCarHireReservationInfo from 'hooks/CarHire/useCarHireReservationInfo'
import { fitMarkersOnMap } from 'lib/maps/googleMapUtils'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import CarHireLocationModalSkeleton from './CarHireLocationModalSkeleton'
import { sortBy } from 'lib/array/arrayUtils'
import { mod } from 'lib/maths/mathUtils'

const MapContainer = styled.div`
  height: ${rem(375)};

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
  }

  .location-map {
    height: ${rem(375)};

    ${mediaQueryUp.tablet} {
      height: ${rem(280)};
    }
  }
`

const Content = styled(VerticalSpacer)`
  padding-top: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const PickUpTypeSection = styled(Pane)`
  padding: ${rem(16)};
`

const pickupTypeIconMap = new Map<App.CarHirePickupType, React.ReactElement>([
  // oxlint-disable-next-line react/jsx-key
  ['office', <LineBuildingIcon />],
  // oxlint-disable-next-line react/jsx-key
  ['shuttle', <LinePlaneFlyIcon />],
  // oxlint-disable-next-line react/jsx-key
  ['terminal', <LineBusAltIcon />],
])

interface OperatingTime {
  dayStart: string
  hours: string
}

function mapOperatingTimeToLabel(operatingTime: App.CarHireLocationOperatingTime): OperatingTime {
  return {
    dayStart: operatingTime.day,
    hours: (!operatingTime.start && !operatingTime.end) ? 'closed' : `${moment(operatingTime.start, 'HH:mm').format(SHORT_TIME_FORMAT_AM_PM)} - ${moment(operatingTime.end, 'HH:mm').format(SHORT_TIME_FORMAT_AM_PM)}`,
  }
}

function getOpeningGroups(location: App.CarHireReservationLocation) {
  const operatingTimes = location.operatingHours.map(mapOperatingTimeToLabel)

  // 0 is usually sunday, -1 shifts 0 to begin monday (*our* first day)
  return sortBy(operatingTimes, time => mod((new Date(time.dayStart).getDay() - 1), 7), 'asc')
}

function getPickUpTypeIcon(locationType: App.CarHireReservationLocation['type']) {
  if (!locationType) {
    return undefined
  }
  return pickupTypeIconMap.get((locationType))
}

function getPickUpTypeLabel(locationType: App.CarHireReservationLocation['type']) {
  if (!locationType) {
    return null
  }
  return pickupTypeLabelMap.get(locationType)
}

interface Props {
  openTab: 'Pick-up' | 'Drop-off'
  orderItem: App.OrderCarHireItem
}

function CarHireItemSummaryLocationModal(props: Props) {
  const { openTab, orderItem } = props
  const tabOptions = ['Pick-up', 'Drop-off']
  const [activeTab, setActiveTab] = useState(openTab === 'Pick-up' ? tabOptions[0] : tabOptions[1])
  const [map, setMap] = useState<google.maps.Map>()
  const isPickUpTabSelected = activeTab === 'Pick-up'

  const reservationId = orderItem.idReservation || ''

  const reservationInfo = useCarHireReservationInfo(reservationId).reservation

  const pickUpLocation = reservationInfo?.pickUp
  const dropOffLocation = reservationInfo?.dropOff
  const differentLocations = pickUpLocation?.id !== dropOffLocation?.id
  const title = differentLocations ? 'Pick-up & Drop-off location' : 'Pick-up & Drop-off locations'

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => { setMap(map) }, [])
  const markers = useMemo(() => {
    if (pickUpLocation?.latitude && dropOffLocation?.latitude && pickUpLocation?.longitude && dropOffLocation?.longitude) {
      const pickUp: App.MapMarker = {
        id: 'location',
        type: 'property',
        latitude: pickUpLocation.latitude,
        longitude: pickUpLocation.longitude,
      }
      const dropOff: App.MapMarker = differentLocations ? {
        id: 'location',
        type: 'property',
        latitude: dropOffLocation.latitude,
        longitude: dropOffLocation.longitude,
      } : pickUp
      return {
        pickUp,
        dropOff,
      }
    }
  }, [dropOffLocation, pickUpLocation, differentLocations])

  const openingGroups = useMemo(() => {
    const pickUp = pickUpLocation && getOpeningGroups(pickUpLocation)
    const dropOff = dropOffLocation && differentLocations ? getOpeningGroups(dropOffLocation) : pickUp
    return {
      pickUp,
      dropOff,
    }
  }, [pickUpLocation, differentLocations, dropOffLocation])

  useEffect(() => {
    if (map && markers) {
      // we need to wait for the open animation to complete
      const timeout = setTimeout(() => {
        fitMarkersOnMap(map, [isPickUpTabSelected ? markers.pickUp : markers.dropOff])
      }, 500)

      return () => clearTimeout(timeout)
    }
  }, [map, isPickUpTabSelected]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasContent = pickUpLocation && markers && openingGroups && dropOffLocation

  return <ModalBase>
    <ModalHeader title={title} />
    <ModalBody>
      {!hasContent && <CarHireLocationModalSkeleton />}
      {hasContent &&
        <>
          {differentLocations &&
            <TabButtonsGroup fit="grow">
              {tabOptions.map((option) => (
                <TabButton
                  isActive={option === activeTab}
                  key={option}
                  size="medium"
                  onClick={() => setActiveTab(option)}
                >
                  {option}
                </TabButton>
              ))}
            </TabButtonsGroup>}
          <MapContainer>
            <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
              <GoogleMap
                id="le-map"
                onLoad={onLoad}
                zoom={16}
                mapContainerClassName="location-map"
              >
                <PropertyMarker
                  marker={isPickUpTabSelected ? markers.pickUp : markers.dropOff}
                  title={isPickUpTabSelected ? pickUpLocation.address : dropOffLocation.address}
                />
              </GoogleMap>
            </EnsureGoogleMapsLoaded>
          </MapContainer>
          <ModalContent>
            <Content gap={24}>
              <div>
                <BodyText variant="large" weight="bold">Address</BodyText>
                <BodyText variant="large">
                  {isPickUpTabSelected ? pickUpLocation?.locationName : dropOffLocation?.locationName}<br />
                  {isPickUpTabSelected ? pickUpLocation.address : dropOffLocation.address}
                </BodyText>
              </div>

              {Boolean(pickUpLocation.type) && Boolean(pickUpLocation.type) &&
                <PickUpTypeSection background="neutral-seven">
                  <BodyTextBlock
                    variant="medium"
                    startIcon={getPickUpTypeIcon(isPickUpTabSelected ? pickUpLocation.type : dropOffLocation.type)}
                  >
                    {getPickUpTypeLabel(isPickUpTabSelected ? pickUpLocation.type : dropOffLocation.type)}
                  </BodyTextBlock>
                </PickUpTypeSection>}

              <div>
                <BodyText variant="large" weight="bold">Opening hours</BodyText>
                {((isPickUpTabSelected ? openingGroups.pickUp : openingGroups.dropOff) || [])
                  .map(day => <BodyText key={day.dayStart} variant="large">
                    <Typography format="titlecase" as="span">
                      {day.dayStart}
                    </Typography>: {day.hours}
                  </BodyText>)}
              </div>
            </Content>
          </ModalContent>
        </>}
    </ModalBody>
  </ModalBase>
}

export default CarHireItemSummaryLocationModal
